import React from 'react'
import {Box} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import GDITextBlock from '../gdiTextBlock'
import {getGDIHideStyles, mergeGDISx} from '../../utils/gdi-styles'

/**
 * Renders an SFCC content asset referenced from the CMS.
 *
 * The CMS block only stores `ContentAssetId`; the actual content asset HTML body
 * is fetched server-side in ssr.js (`resolveContentAssetBodies`) and injected as
 * `RenderedBody` (or `RenderedBodyError` when the fetch fails).
 *
 * The body is rendered through `GDITextBlock` so the content asset markup runs
 * through the exact same pipeline as authored CMS text — i.e. all GDI inline
 * `{...}` directives (links, tooltips, brandName, readMore, textStyle, vouchers,
 * newsletter form, inline map, dividers, dynamic tokens, …) are interpreted and
 * hydrated identically. This keeps a single source of truth for directive parsing.
 */
const GDIContentAssetBlock = ({
    HideBlockOn,
    ContentAssetId,
    RenderedBody,
    RenderedBodyError,
    ...props
}) => {
    // Body resolved — render it through GDITextBlock.
    if (RenderedBody) {
        return (
            <GDITextBlock
                HideBlockOn={HideBlockOn}
                TextForWeb={RenderedBody}
                data-block-type="GDIContentAssetBlock"
                data-content-asset-id={ContentAssetId || undefined}
                {...props}
            />
        )
    }

    // Body could not be fetched (e.g. the OCAPI Shop `content` resource has not
    // been allowlisted in Business Manager). Render a visible warning in place of
    // the component so the misconfiguration is obvious rather than silently empty.
    if (RenderedBodyError) {
        const hideStyles = getGDIHideStyles(HideBlockOn)
        if (hideStyles.doNotLoadComponent) return null

        const {sx: propsSx, ...restProps} = props
        return (
            <Box
                data-block-type="GDIContentAssetBlock"
                data-content-asset-id={ContentAssetId || undefined}
                role="alert"
                sx={mergeGDISx(
                    {
                        p: 4,
                        border: '1px dashed',
                        borderColor: 'orange.400',
                        backgroundColor: 'orange.50',
                        color: 'orange.800',
                        borderRadius: 'md',
                        textStyle: 'bodySmRegular'
                    },
                    hideStyles.sx,
                    propsSx
                )}
                {...restProps}
            >
                {`Content asset${
                    ContentAssetId ? ` "${ContentAssetId}"` : ''
                } could not be loaded. ${RenderedBodyError}`}
            </Box>
        )
    }

    // No body and no error (asset offline/empty) — render nothing.
    return null
}

GDIContentAssetBlock.propTypes = {
    HideBlockOn: PropTypes.string,
    ContentAssetId: PropTypes.string,
    RenderedBody: PropTypes.string,
    RenderedBodyError: PropTypes.string,
    GDIContentAssetBlockAudienceTargeting: PropTypes.object
}

export default GDIContentAssetBlock
